import React, { useState, useEffect } from "react";
import styles from "./style.module.css";
import { useLocation } from "react-router-dom";
import { linkReplaceFn } from "../../utils/custom_funtions";
import useMathjax from "../../hooks/useMathjax";
import { getOrGenerateQRCode } from "../../utils/qrCodeUtils";
import footerEnglish from "../../assets/img/footer2.jpg";
import footerHindi from "../../assets/img/black_footer.jpg";
import header from "../../assets/img/image.png"

const QuestionModuleRender = () => {
  const location = useLocation();
  const responseData = location.state?.responseData || [];
  const language = location.state?.language || "english";
  const [qrCodes, setQrCodes] = useState({});
  const [qrCodesLoading, setQrCodesLoading] = useState(true);
 
  
  // Re-typeset MathJax when questions or language change (covers options)
  useMathjax(responseData, language);
  console.log(responseData)
  

  // Generate QR codes for all questions
  useEffect(() => {
    const generateAllQRCodes = async () => {
      setQrCodesLoading(true);
      const qrCodeMap = {};
      
      for (const question of responseData) {
        const questionId = question.qid || question.id || question.question_id;
        if (questionId) {
          try {
            const qrCodeDataURL = await getOrGenerateQRCode(question, questionId);
            if (qrCodeDataURL) {
              qrCodeMap[questionId] = qrCodeDataURL;
            }
          } catch (error) {
            console.error(`Error generating QR code for question ${questionId}:`, error);
          }
        }
      }
      
      setQrCodes(qrCodeMap);
      setQrCodesLoading(false);
    };

    if (responseData.length > 0) {
      generateAllQRCodes();
    } else {
      setQrCodesLoading(false);
    }
  }, [responseData]);

  const setPrintPageSize = (size) => {
    let style = document.getElementById("dynamic-print-page-size");
  
    if (!style) {
      style = document.createElement("style");
      style.id = "dynamic-print-page-size";
      document.head.appendChild(style);
    }
  
    style.innerHTML = `
      @media print {
        @page {
          size: ${size};
          padding:.5in .6in .5in .6in !important;
          
        }
      }
    `;
  };
  
  useEffect(() => {
    // Bulk Question Request size
    setPrintPageSize("8.26in 10.1in");
  
    return () => {
      // Cleanup when leaving page
      const style = document.getElementById("dynamic-print-page-size");
      if (style) style.remove();
    };
  }, []);
  

  // Extract options from HTML string (preserve MathJax markup)
  const extractOptions = (htmlString) => {
    if (!htmlString) return [];
    return htmlString.split("@@@").map((html) => {
      const doc = new DOMParser().parseFromString(html, "text/html");
      return doc.body.innerHTML.trim(); // Keep inline HTML for MathJax
    });
  };

  // Group questions by exercise level, type, and sub-topic with section persistence
  const groupByExerciseAndType = () => {
    const grouped = {};
    let currentExerciseLevel = null;
    let currentQuestionType = null;
    let currentSubTopic = null;
    
    responseData.forEach((question) => {
      if (question.section) {
        currentExerciseLevel = question.section;
      }
      if (question.section_type) {
        currentQuestionType = question.section_type;
      }
      if (question.sub_topics) {
        currentSubTopic = question.sub_topic_name
          ? `${question.sub_topic_name}${question.sub_topics}`
          : question.sub_topics;
      }
      
      const exerciseLevel = currentExerciseLevel || "Exercise-1(Level A)";
      const questionType = currentQuestionType || "Single Correct";
      console.log(questionType);
      const subTopicName =
        currentSubTopic ||
        question.sub_topics ||
        question.sub_topic_name ||
        "General";
      
      if (!grouped[exerciseLevel]) {
        grouped[exerciseLevel] = {};
      }
      if (!grouped[exerciseLevel][questionType]) {
        grouped[exerciseLevel][questionType] = {};
      }
      if (!grouped[exerciseLevel][questionType][subTopicName]) {
        grouped[exerciseLevel][questionType][subTopicName] = [];
      }
      
      grouped[exerciseLevel][questionType][subTopicName].push(question);
    });
    
    return grouped;
  };

  const groupedQuestions = groupByExerciseAndType();

  // Build option list for each question so layout can be managed easily
  const buildOptionList = (question) => {
    const isBiology = responseData?.[0]?.sub_name === "Biology";
  
    const optionLabels = isBiology
      ? ["1", "2", "3", "4"]
      : ["A", "B", "C", "D"];
  
    const englishOptionChunks = extractOptions(question.eng_option);
    const hindiOptionChunks = extractOptions(question.hindi_option);
  
    const englishFallbacks = [
      question.option_a,
      question.option_b,
      question.option_c,
      question.option_d,
    ];
  
    const hindiFallbacks = [
      question.option_a_hindi,
      question.option_b_hindi,
      question.option_c_hindi,
      question.option_d_hindi,
    ];
  
    return optionLabels.map((label, idx) => ({
      label,
      value:
        language === "english"
          ? englishOptionChunks[idx] || englishFallbacks[idx]
          : hindiOptionChunks[idx] || hindiFallbacks[idx],
    }));
  };

  // Render option content with MathJax-ready HTML
  const renderOptionContent = (value) => {
    if (!value) return <span className={styles.optionText}>&nbsp;</span>;
    return (
      <span
        className={styles.optionText}
        dangerouslySetInnerHTML={{ __html: value }}
      />
    );
  };

  // Allow per-question option layout selection with stable class names
  const getOptionLayoutClass = (question) => {
    const layoutHint =
      question.optionLayout ||
      question.option_layout ||
      question.optionColumns ||
      question.option_columns;
    
    const normalized =
      typeof layoutHint === "string"
        ? layoutHint.toLowerCase()
        : layoutHint;
    
    if (normalized === 1 || normalized === "1" || normalized === "one" || normalized === "single") {
      return "option-cols-1";
    }
    
    if (normalized === 4 || normalized === "4" || normalized === "four" || normalized === "all") {
      return "option-cols-4";
    }
    
    // Default (and also handles explicit "2" / "two")
    return "option-cols-2";
  };

  // Render a single question
  const renderQuestion = (question, index, columnsTotal, type, globalIndex) => {
    const questionId = question.qid || question.id || question.question_id;
    const qrCodeDataURL = questionId ? qrCodes[questionId] : null;
    // Use globalIndex for display number if provided, otherwise use index
    const displayNumber = globalIndex !== undefined ? globalIndex : index;
    const options = buildOptionList(question);
    const optionLayoutClass = getOptionLayoutClass(question);
    
    return (
      <div key={`q-${displayNumber}`} className={`${styles.questionItem}`}>
        {/* Question Row */}
        <div className={styles.questionRow}>
          <div className={styles.questionNumber}>{displayNumber + 1}.</div>
          <div 
            className={styles.questionText} 
            dangerouslySetInnerHTML={{ 
              __html: language === "english" ? question.cq_text : question.q_text_hindi
            }} 
          />
          
          {/* QR Code - Show generated QR code or existing one */}
          <div className={styles.qrCode}>
            {qrCodesLoading ? (
              <div style={{ 
                width: '60px', 
                height: '60px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                fontSize: '10px',
                color: '#666'
              }}>
                Loading...
              </div>
            ) : (qrCodeDataURL || question.qr_code) ? (
              <img 
                src={qrCodeDataURL || question.qr_code} 
                alt="QR Code" 
                title={`Scan to view question ${questionId || displayNumber + 1}`}
              />
            ) : null}
          </div>
        </div>

        
        
        {/* Options Grid */}
        {question.type !== "Integer-Type" && (
          <div
            className={`${styles.optionsGrid} option-grid ${optionLayoutClass}`}
            data-option-layout={optionLayoutClass}
          >
            {options.map((option) => (
              <div key={option.label} className={`${styles.option} option-item`}>
                <span className={styles.optionLabel}>({option.label})</span>
                {renderOptionContent(option.value)}
              </div>
            ))}
          </div>
        )}
        
        {/* Reference Citation */}
        <div className={styles.reference}>
          {(question.correct || question.incorrect) ? `{C: ${question.correct}%, I.C ${question.incorrect}%, U.A ${question.unattempted}%}` : ""} {question.examName && question.examYear ? `${question.examName} ${question.examYear}` : ""}
        </div>
      </div>
    );
  };

  // Function to get the answer value from different possible field names
  const getAnswerValue = (question) => {
    // Check multiple possible field names for the answer
    const possibleFields = ['answer', 'correct_option', 'correct_answer', 'correct_ans', 'correctAnswer'];
    
    for (const field of possibleFields) {
      if (question[field] !== undefined && question[field] !== null) {
        return question[field];
      }
    }
    
    // If no answer field is found, check if there's a "correct" field that might contain the answer index (0,1,2,3)
    if (question.correct !== undefined) {
      // Convert numeric index to letter (0->A, 1->B, etc.)
      const answerLetters = ['A', 'B', 'C', 'D'];
      return answerLetters[question.correct] || question.correct;
    }
    
    return ""; // Return empty string if no answer field is found
  };

  // Function to render all section answer keys at the bottom
  const renderAllSectionAnswerKeys = () => {
    // Create an array to hold all section answer key components
    const allSectionAnswers = [];

    // Loop through each exercise/section
    Object.entries(groupedQuestions).forEach(([exerciseLevel, questionTypes]) => {
      // Loop through each question type in this section
      Object.entries(questionTypes).forEach(([type, subTopics]) => {
        const questions = Object.values(subTopics).flat();
        const sectionTitle = exerciseLevel;
        
        // Calculate how many rows we need (8 answers per row)
        const rows = Math.ceil(questions.length / 8);
        const answerRows = [];
        
        for (let i = 0; i < rows; i++) {
          const startIdx = i * 8;
          const rowQuestions = questions.slice(startIdx, startIdx + 8);
          
          answerRows.push(
            <div key={`row-${sectionTitle}-${i}`} className={styles.answerRow}>
              {rowQuestions.map((question, idx) => {
                const questionNumber = startIdx + idx + 1;
                const answerValue = getAnswerValue(question);
                
                return (
                  <div key={`answer-${sectionTitle}-${questionNumber}`} className={styles.answerItem}>
                    {questionNumber}. <span>{answerValue}</span>
                  </div>
                );
              })}
            </div>
          );
        }
        
        if (questions.length > 0) {
          allSectionAnswers.push(
            <div key={sectionTitle} className={styles.answerKeySection}>
              <h3 className={styles.answerKeyTitle}>{sectionTitle}</h3>
              <div className={styles.answerKeyContainer}>
                {answerRows}
              </div>
            </div>
          );
        }
      });
    });
    
    if (allSectionAnswers.length === 0) {
      return null;
    }
    
    return (
      <div className={styles.masterAnswerKeyContainer}>
        <h2 className={styles.masterAnswerKeyTitle}>Answer Keys</h2>
        {allSectionAnswers}
      </div>
    );
  };
  
  // New function to render questions with print-optimized layout
  const renderQuestionsForPrint = (questions, type, startingIndex = 0) => {
    return questions.map((question, idx) =>
      renderQuestion(question, startingIndex + idx, questions.length, type, startingIndex + idx)
    );
  };

  // Track global question index across all sections
  let globalQuestionIndex = 0;

  const footerImage = language === "hindi" ? footerHindi : footerEnglish;

  return (
    <div className={`${styles.container} container-fluid ${styles.printable} print-page print-bulk-question-request`}>
     {/* Blank page at the start */}
     <div className={styles.blankPage}></div>
     
     <div className={styles.printHeader}>
       <img src={header} alt="Header" className={styles.headerImage} />
     </div>
      <div className={styles.printContent}>
      
      {/* Questions Section */}
      {Object.entries(groupedQuestions).map(([exerciseLevel, questionTypes]) => {
        const exerciseStartIndex = globalQuestionIndex;
        
        return (
          <div key={exerciseLevel} className={styles.exerciseSection}>
            
            <div className={styles.redLine} />
            <div className={styles.exerciseTitleRow}>
              <div className={styles.exerciseTitle}>{exerciseLevel}</div>
              <div className={styles.questionType}>
                {Object.keys(questionTypes)[0] || "Single Correct"}
              </div>
            </div>
            
            <div className={styles.redLine1} />
            
            {/* Questions by type */}
            {Object.entries(questionTypes).map(([type, subTopics]) => {
              const totalQuestions = Object.values(subTopics).reduce((sum, list) => sum + list.length, 0);
              globalQuestionIndex += totalQuestions;
              
              let typeQuestionOffset = 0;
              const typeContent = [];
              
              Object.entries(subTopics).forEach(([subTopicName, questions]) => {
                const firstQuestion = questions[0] || {};
                const displaySubTopic =
                  firstQuestion.sub_topics ||
                  firstQuestion.sub_topic_name ||
                  subTopicName;

                const subjectDisplayName = displaySubTopic;

                typeContent.push(
                  <div key={`subtopic-${type}-${subTopicName}`} className={styles.subjectName}>
                    {subjectDisplayName}
                  </div>
                );
                
                typeContent.push(
                  ...renderQuestionsForPrint(questions, type, typeQuestionOffset)
                );
                
                typeQuestionOffset += questions.length;
              });
              
              return (
                <div key={type}>
                  <div className={styles.printOptimizedLayout} style={{ columnCount: 2 }}>
                    {typeContent}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
      
      {/* All Answer Keys at the bottom */}
      {renderAllSectionAnswerKeys()}
      </div>

      {/* Print Footer */}
      <div className={styles.printFooter}>
       
          <img
            src={footerImage}
            alt="Footer"
            style={{ width: "100%", height: "35px",  }}
          />
        </div>
      
    </div>
  );
};

export default QuestionModuleRender;